import {
  GRID_ROOT_GROUP_ID,
  GridGroupNode,
  GridRowId,
  GridRowTreeConfig,
  GridTreeNode
} from "@mui/x-data-grid";
import {
  GridRowSelectionModel
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import _ from "lodash";
import { MutableRefObject, useContext, useEffect, useState } from "react";
import { DataGridContext } from "../context/DataGridContext";

// https://stackblitz.com/edit/react-ucmhy1?file=Demo.tsx

export interface UseExpandAndCollapseProps {
  apiRef: MutableRefObject<GridApiPremium>;
}

/**
 * Custom hook to manage row expansion and collapse in a data grid.
 * 
 * NOTE: This is a special hook that runs outside the grid's state management. As such the apiRef is not initialized 
 * until the grid is mounted and will start off as undefined in the first render(s). This means a null check is required 
 * on apiRef? when accessing it before the grid is mounted.
 * 
 * Most other hooks run inside the grid's state management where apiRef will be initialized before the hook runs.
 * 
 * @param apiRef - Reference to the grid API.
 * @returns An object containing the current expand level and functions to expand/collapse rows.
 */
const useExpandAndCollapse = (apiRef: MutableRefObject<GridApiPremium>) => {
  //console.log("useExpandAndCollapse -> apiRef: ", apiRef);
  const [expandLevel, setExpandLevel] = useState<{ [key: GridRowId]: number; }>({});
  const [expandAllDisabled, setExpandAllDisabled] = useState<boolean>(false);
  const [collapseAllDisabled, setCollapseAllDisabled] = useState<boolean>(true);
  const dataGridContext = useContext(DataGridContext);

  useEffect(() => {
    const handleReset = () => {
      setExpandLevel({});
      setExpandAllDisabled(false);
      setCollapseAllDisabled(true);
    };

    // TODO: convert to this a custom event so we can control when the reset happens
    return apiRef?.current?.subscribeEvent("rowsSet", handleReset);
  }, [apiRef]);

  const onExpandRow = async (event: any, rowId: GridRowId) => {
    event.stopPropagation();

    const currentNode = apiRef.current.getRowNode<GridGroupNode>(rowId);
    if (currentNode?.type !== "group") {
      return;
    }

    const newExpandLevel = getRowExpandedLevel(rowId) + 1;
    console.log("onExpandRow -> rowId: ", rowId, "newExpandLevel: ", newExpandLevel);

    const newGridRowSelectionModel: GridRowSelectionModel = [];//[...rowSelectionModel];
    const treeState = { ...apiRef.current.state.rows.tree };

    if (newExpandLevel === 1) {
      const newNode = { ...currentNode, childrenExpanded: true } as GridTreeNode;
      treeState[rowId] = newNode;
    } else {

      currentNode?.children?.forEach((childId: GridRowId) => {
        propagateExpansionDown(apiRef, treeState, childId, true, newGridRowSelectionModel, 1, newExpandLevel);
      });
    }
    apiRef.current.setState((state) => ({ ...state, rows: { ...state.rows, tree: treeState } }));
    apiRef.current.publishEvent("rowExpansionChange", { ...treeState[GRID_ROOT_GROUP_ID] as GridGroupNode, childrenExpanded: true, expandLevel: newExpandLevel } as any);
    //setRowSelectionModel(newGridRowSelectionModel);

    //setExpandDisabled(!hasCollapsedChildren);
    //setCollapseDisabled(false);
  };

  const onCollapseRow = async (event: any, rowId: GridRowId) => {
    event.stopPropagation();

    const currentNode = apiRef.current.getRowNode<GridGroupNode>(rowId);
    if (currentNode?.type !== "group") {
      return;
    }

    const newCollapseLevel = Math.max(0, getRowExpandedLevel(rowId) - 1);
    console.log("onCollapseRow -> rowId: ", rowId, "newCollapseLevel: ", newCollapseLevel);

    const newGridRowSelectionModel: GridRowSelectionModel = [];//[...rowSelectionModel];
    const treeState = { ...apiRef.current.state.rows.tree };

    if (newCollapseLevel === 0) {
      const newNode = { ...currentNode, childrenExpanded: false } as GridTreeNode;
      treeState[rowId] = newNode;
    } else {

      currentNode?.children?.forEach((childId: GridRowId) => {
        propagateExpansionDown(apiRef, treeState, childId, false, newGridRowSelectionModel, 0, newCollapseLevel - 1);
      });
    }
    apiRef.current.setState((state) => ({ ...state, rows: { ...state.rows, tree: treeState } }));
    apiRef.current.publishEvent("rowExpansionChange", { ...treeState[GRID_ROOT_GROUP_ID] as GridGroupNode, childrenExpanded: false, expandLevel: newCollapseLevel } as any);
    //setRowSelectionModel(newGridRowSelectionModel);

    //setExpandDisabled(false);
    //setCollapseDisabled(newExpandLevel == 0);
  };

  const onExpandAll = async (event: any, rowId: GridRowId = GRID_ROOT_GROUP_ID) => {
    event.stopPropagation();

    const currentNode = apiRef.current.getRowNode<GridGroupNode>(rowId);

    const newExpandLevel = (expandLevel[rowId] || 0) + 1;
    const newGridRowSelectionModel: GridRowSelectionModel = [];//[...rowSelectionModel];
    const treeState = { ...apiRef.current.state.rows.tree };

    currentNode?.children?.forEach((childId: GridRowId) => {
      propagateExpansionDown(apiRef, treeState, childId, true, newGridRowSelectionModel, 0, newExpandLevel);
    });
    apiRef.current.setState((state) => ({ ...state, rows: { ...state.rows, tree: treeState } }));
    apiRef.current.publishEvent("rowExpansionChange", treeState[GRID_ROOT_GROUP_ID] as GridGroupNode);
    //setRowSelectionModel(newGridRowSelectionModel);

    setExpandLevel({ ...expandLevel, [rowId]: newExpandLevel });
    const hasCollapsedChildren = gridHasCollapsedChildren(GRID_ROOT_GROUP_ID);
    setExpandAllDisabled(!hasCollapsedChildren);
    setCollapseAllDisabled(false);
  };

  const onCollapseAll = async (event: any, rowId: GridRowId = GRID_ROOT_GROUP_ID) => {
    event.stopPropagation();

    const currentNode = apiRef.current.getRowNode<GridGroupNode>(rowId);
    const newExpandLevel = Math.max(0, (expandLevel[rowId] || 0) - 1);
    const newGridRowSelectionModel: GridRowSelectionModel = [];//[...rowSelectionModel];
    const treeState = { ...apiRef.current.state.rows.tree };

    currentNode?.children?.forEach((childId: GridRowId) => {
      propagateExpansionDown(apiRef, treeState, childId, false, newGridRowSelectionModel, 0, newExpandLevel);
    });
    apiRef.current.setState((state) => ({ ...state, rows: { ...state.rows, tree: treeState } }));
    apiRef.current.publishEvent("rowExpansionChange", treeState[GRID_ROOT_GROUP_ID] as GridGroupNode);
    //setRowSelectionModel(newGridRowSelectionModel);

    setExpandLevel({ ...expandLevel, [rowId]: newExpandLevel });
    setExpandAllDisabled(false);
    setCollapseAllDisabled(newExpandLevel === 0);
  };

  const propagateExpansionDown = (
    apiRef: MutableRefObject<GridApiPremium>,
    treeState: GridRowTreeConfig,
    rowId: GridRowId,
    isExpanded: boolean,
    gridRowSelectionModel: GridRowSelectionModel,
    currentLevel: number,
    maxLevel?: number
  ) => {
    const rowNode = apiRef.current.getRowNode<GridGroupNode>(rowId);

    if (isExpanded) {
      if (!_.isNil(maxLevel) && maxLevel! > 0 && currentLevel < maxLevel!) {
        //console.log("Expanding -> rowId: ", rowId, "expanded: ", isExpanded);
        if (rowNode?.type === "group") {
          const currentNode = apiRef.current.getRowNode(rowId);
          const newNode = { ...currentNode, childrenExpanded: true } as GridTreeNode;
          treeState[rowId] = newNode;
        }
        //gridRowSelectionModel.push(rowId);
      } else if (!_.isNil(maxLevel) && maxLevel! > 0 && currentLevel == maxLevel!) {
        return;
      }
    } else {
      if (!_.isNil(maxLevel) && currentLevel >= maxLevel!) {
        //console.log("Collapsing -> rowId: ", rowId, "expanded: ", isExpanded);
        if (rowNode?.type === "group") {
          const currentNode = apiRef.current.getRowNode(rowId);
          const newNode = { ...currentNode, childrenExpanded: false } as GridTreeNode;
          treeState[rowId] = newNode;
        }
        //gridRowSelectionModel = gridRowSelectionModel.filter((id) => id !== rowId);
      }
    }

    rowNode?.children?.forEach((childId: GridRowId) => {
      propagateExpansionDown(apiRef, treeState, childId, isExpanded, gridRowSelectionModel, currentLevel + 1, maxLevel);
    });
  };

  const hasCollapsedChildren = (rowId: GridRowId, callbackFn: (node: GridTreeNode) => boolean): boolean => {
    const rowNode = apiRef.current.getRowNode(rowId);
    if (!rowNode) {
      return false;
    }

    if (callbackFn(rowNode)) {
      return true;
    }

    //const rowIds = apiRef.current.getRowGroupChildren({ groupId: rowId });
    const rowIds = (rowNode as GridGroupNode)?.children || [];

    for (const childId of rowIds) {
      if (hasCollapsedChildren(childId, callbackFn)) {
        return true;
      }
    }

    return false;
  };

  const gridHasCollapsedChildren = (rowId: GridRowId): boolean => {
    return hasCollapsedChildren(rowId, (node: GridTreeNode) => {
      if (node.type === "group" && !node.childrenExpanded) {
        return true;
      }
      return false;
    });
  };

  const traverse = (rowId: GridRowId, callbackFn: (node: GridTreeNode, currentLevel: number) => number, currentLevel: number = 0): number => {
    const rowNode = apiRef.current.getRowNode(rowId);
    if (!rowNode) {
      return currentLevel;
    }
    const rowIds = apiRef.current.getRowGroupChildren({ groupId: rowId, skipAutoGeneratedRows: false });

    if (callbackFn(rowNode, currentLevel + 1) === -1) {
      return currentLevel;
    }

    let expandedLevel = currentLevel;
    for (const childId of rowIds) {
      const newExpandLevel = traverse(childId, callbackFn, currentLevel + 1);
      if (newExpandLevel > expandedLevel) {
        expandedLevel = newExpandLevel;
      }
    }

    console.log("traverse: ", rowNode, expandedLevel);
    return expandedLevel;
  };

  const getRowExpandedLevel = (rowId: GridRowId): number => {
    return traverse(rowId, (node: GridTreeNode, currentLevel: number) => {
      if (node.type === "group" && node.childrenExpanded) {
        return currentLevel;
      }
      return -1;
    });
  };

  return { onExpandAll, onCollapseAll, onExpandRow, onCollapseRow, expandAllDisabled, collapseAllDisabled };
};

export default useExpandAndCollapse;